import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PaynowService } from '../api/paynowService';
import PaymentHistoryTable from '../components/payments/PaymentHistoryTable';
import FeatureGuard from '../components/common/FeatureGuard';
import { useDecodedToken } from '../utils/useDecodedToken';
import UserTypeIndicator from '../components/common/UserTypeIndicator';
import { useLocation } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import { api } from '../api/client';

interface PaymentStats {
  totalPayments: number;
  successfulPayments: number;
  failedPayments: number;
  totalAmount: number;
  successRate: number;
}

interface LoanPaymentRequest {
  id: number;
  amount: number;
  currency: string;
  description: string;
  paymentType: string;
  dueDate?: string;
  status: string;
  isOutstanding: boolean;
  loanId?: string;
  trackingNumber?: string;
}

const Payments: React.FC = () => {
  const user = useDecodedToken();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loanPaymentRequests, setLoanPaymentRequests] = useState<LoanPaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trackingNumberInput, setTrackingNumberInput] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<{[key: string]: string}>({});
  const [checkingStatus, setCheckingStatus] = useState<{[key: string]: boolean}>({});

  // Hard authentication check - redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {

      navigate('/login');
      return;
    }

  }, []);

  // Check if we have loan context from navigation
  const loanContext = location.state as {
    loanId?: string;
    trackingNumber?: string;
    paymentRequest?: LoanPaymentRequest;
  };

  const fetchStats = async () => {
    // Check authentication before making any API calls
    if (!isAuthenticated()) {

      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Only try to fetch stats if user has account access (Fineract)
      if (user?.hasFullAccess) {
        try {
          const statsData = await PaynowService.getPaymentStats();
          setStats(statsData);
        } catch (err: any) {

          // Don't show error for missing stats - it's optional
          setStats(null);
        }
      } else {

        // For users without account access, focus on loan application payments
        setStats(null);
      }
    } catch (err: any) {

      // Don't show error for missing stats
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanPaymentRequests = async () => {
    // Check authentication before making any API calls
    if (!isAuthenticated()) {

      return;
    }

    try {
      let requests: LoanPaymentRequest[] = [];
      
      if (user?.trackingNumber) {
        // Fetch payment requests for user's loan
        const response = await PaynowService.getLoanPaymentRequests(user.trackingNumber);
        requests = response || [];
      } else if (loanContext?.trackingNumber) {
        // Fetch payment requests for specific loan from context
        const response = await PaynowService.getLoanPaymentRequests(loanContext.trackingNumber);
        requests = response || [];
      }
      
      setLoanPaymentRequests(requests);
    } catch (err: any) {

      setLoanPaymentRequests([]);
    }
  };

  const fetchLoanPaymentRequestsForTracking = async (trackingNumber: string) => {
    // Check authentication before making any API calls
    if (!isAuthenticated()) {

      return;
    }

    try {

      const response = await PaynowService.getLoanPaymentRequests(trackingNumber);
      const requests = response || [];
      setLoanPaymentRequests(requests);
      
      // Update loan context with the tracking number
      if (loanContext?.trackingNumber !== trackingNumber) {
        // You could update the context here if needed

      }
    } catch (err: any) {

      setLoanPaymentRequests([]);
    }
  };

  useEffect(() => {
    // Only fetch data if authenticated
    if (isAuthenticated()) {
      fetchStats();
      fetchLoanPaymentRequests();
      
      // Initialize tracking number input with loan context if available
      if (loanContext?.trackingNumber) {
        // Ensure tracking number is always a string
        setTrackingNumberInput(String(loanContext.trackingNumber));
      }
    }
  }, [user, loanContext]);

  // Auto-switch to loan payments tab if we have loan context
  useEffect(() => {
    // Only switch tabs if authenticated
    if (isAuthenticated() && (loanContext?.trackingNumber || loanContext?.paymentRequest)) {
      // For users without account access, set to tab 0 (Loan Payment Requests)
      // For users with account access, set to tab 1 (Loan Payment Requests)
      setActiveTab(user?.hasFullAccess ? 1 : 0);
    }
  }, [loanContext, user?.hasFullAccess]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPaymentTypeIcon = (paymentType?: string) => {
    const safeType = String(paymentType || '').toLowerCase();
    switch (safeType) {
      case 'processing_fee':
        return <AssignmentIcon />;
      case 'loan_repayment':
        return <PaymentIcon />;
      case 'insurance_fee':
        return <AssignmentIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const getPaymentTypeColor = (paymentType?: string) => {
    const safeType = String(paymentType || '').toLowerCase();
    switch (safeType) {
      case 'processing_fee':
        return 'warning';
      case 'loan_repayment':
        return 'primary';
      case 'insurance_fee':
        return 'info';
      default:
        return 'default';
    }
  };

  const checkPaymentStatus = async (paymentId: string) => {
    if (!isAuthenticated()) return;
    
    setCheckingStatus(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const statusResponse = await PaynowService.pollPaymentStatus(paymentId);
      setPaymentStatus(prev => ({ 
        ...prev, 
        [paymentId]: statusResponse.paynowStatus || statusResponse.status 
      }));
    } catch (err: any) {
      console.error('Failed to check payment status:', err);
      setPaymentStatus(prev => ({ 
        ...prev, 
        [paymentId]: 'Error checking status' 
      }));
    } finally {
      setCheckingStatus(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  // Automatic payment polling after initiation
  const startPaymentPolling = (paymentReference: string) => {

    
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await PaynowService.pollPaymentStatus(paymentReference);

        
        if (statusResponse.paynowStatus === 'PAID' || statusResponse.paynowStatus === 'DELIVERED') {
          // Payment completed successfully
          setSuccess(`✅ Payment completed! Reference: ${paymentReference}`);
          clearInterval(pollInterval);
          
          // Refresh payment data
          setTimeout(() => {
            fetchLoanPaymentRequests();
          }, 1000);
        } else if (statusResponse.paynowStatus === 'FAILED' || statusResponse.paynowStatus === 'CANCELLED') {
          // Payment failed
          setError(`Payment failed: ${statusResponse.paynowStatus}`);
          clearInterval(pollInterval);
        } else {
          // Still pending - update status message
          setSuccess(`Payment in progress... Status: ${statusResponse.paynowStatus || 'PENDING'}. Reference: ${paymentReference}`);
        }
      } catch (err) {
        console.error('[PAYMENTS] Error polling payment status:', err);
        // Continue polling on error
      }
    }, 5000); // Poll every 5 seconds

    // Stop polling after 10 minutes (60 * 10 = 600 seconds)
    setTimeout(() => {
      clearInterval(pollInterval);

      if (success.includes('Payment in progress')) {
        setSuccess(`Payment status check timed out. Please check manually. Reference: ${paymentReference}`);
      }
    }, 600000);
  };

  const handleMakePayment = async (paymentRequest: LoanPaymentRequest) => {
    // Double-check authentication before allowing payment
    if (!isAuthenticated()) {

      sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
      navigate('/login');
      return;
    }

    // Clear any existing messages
    setError('');
    setSuccess('');

    // Open a blank tab synchronously to avoid popup blockers
    const pendingWindow = window.open('', '_blank');
    if (pendingWindow) {
      pendingWindow.document.write('<p style="font-family: sans-serif;">Opening PayNow payment page...</p>');
    }

    try {

      
      // Get user info for payment
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
      const userEmail = userInfo.email || user?.email || '';
      const userName = userInfo.name || user?.preferred_username || '';
      
      // Determine payment type based on description
      let paymentType: 'PROCESSING_FEE' | 'LOAN_REPAYMENT' | 'INSURANCE_FEE' = 'PROCESSING_FEE';
      if (paymentRequest.description.toLowerCase().includes('repayment')) {
        paymentType = 'LOAN_REPAYMENT';
      } else if (paymentRequest.description.toLowerCase().includes('insurance')) {
        paymentType = 'INSURANCE_FEE';
      }
      
      // Prepare payment data for YOUR backend payment processing
      const paymentData = {
        amount: paymentRequest.amount,
        currency: paymentRequest.currency || 'USD',
        description: paymentRequest.description,
        payerEmail: userEmail,
        payerPhone: '+263771234567', // Default phone number - you can make this configurable
        paymentType: paymentType,
        clientLoanId: paymentRequest.loanId ? parseInt(paymentRequest.loanId) : null, // Try null instead of 0
        trackingNumber: paymentRequest.trackingNumber ? parseInt(paymentRequest.trackingNumber) : null, // Convert to number
        additionalInfo: `Payment for loan application ${paymentRequest.trackingNumber || 'N/A'}`
      };



      // Call YOUR backend to process the actual payment
      const response = await api.post('/api/v1/payments/initiate', paymentData);
      

      
      // Check if this is a mock payment (no redirect URL means it's completed immediately)
      if (!response.data.redirectUrl && response.data.successful) {
        // This is a mock payment that completed immediately
        setSuccess(`✅ MOCK PAYMENT COMPLETED! This is a test payment that was processed immediately. No actual money was transferred. Reference: ${response.data.paynowReference || response.data.paymentReference || 'N/A'}`);

        
        if (pendingWindow && !pendingWindow.closed) {
          pendingWindow.close();
        }
      } else if (response.data.redirectUrl) {
        // Real payment - navigate to payment gateway
        const targetUrl = response.data.redirectUrl as string;
        if (pendingWindow && !pendingWindow.closed) {
          try {
            pendingWindow.location.href = targetUrl;
          } catch(e) {
            // Fallback if the window was blocked from navigation
            window.location.assign(targetUrl);
          }
        } else {
          // Popup blocked - fallback to same-tab navigation
          window.location.assign(targetUrl);
        }
        setSuccess(`Payment initiated! Opening payment page... Reference: ${response.data.paynowReference || response.data.paymentReference || 'N/A'}`);
        
        // Start automatic polling for this payment
        const paymentRef = response.data.paynowReference || response.data.paymentReference;
        if (paymentRef) {
          startPaymentPolling(paymentRef);
        }
      } else if (response.data.instructions) {
        // Show payment instructions
        setSuccess(`Payment initiated! ${response.data.instructions} Reference: ${response.data.paynowReference || response.data.paymentReference || 'N/A'}`);
        if (pendingWindow && !pendingWindow.closed) {
          pendingWindow.close();
        }
        
        // Start automatic polling for this payment
        const paymentRef = response.data.paynowReference || response.data.paymentReference;
        if (paymentRef) {
          startPaymentPolling(paymentRef);
        }
      } else {
        // Fallback success message
        setSuccess(`Payment initiated successfully! Reference: ${response.data.paynowReference || response.data.paymentReference || 'N/A'}`);
        if (pendingWindow && !pendingWindow.closed) {
          pendingWindow.close();
        }
        
        // Start automatic polling for this payment
        const paymentRef = response.data.paynowReference || response.data.paymentReference;
        if (paymentRef) {
          startPaymentPolling(paymentRef);
        }
      }
      
      // Refresh payment requests to show updated status
      setTimeout(() => {
        fetchLoanPaymentRequests();
      }, 1000);
      
         } catch (err: any) {
      console.error('[PAYMENTS] Failed to initiate payment:', err);
      
      // Close the pending window on error
      try {
        if (pendingWindow && !pendingWindow.closed) pendingWindow.close();
      } catch {}
      
      // Extract error message properly, handling both string and object responses
      let errorMsg = 'Failed to initiate payment';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.message) {
          errorMsg = err.response.data.message;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else {
          // If it's an object, try to extract a meaningful message
          errorMsg = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      
      // Clear error after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  // Normalize to always be an array to avoid runtime map errors if API returns an object
  const normalizedLoanPaymentRequests: LoanPaymentRequest[] = Array.isArray(loanPaymentRequests)
    ? loanPaymentRequests
    : (loanPaymentRequests ? [loanPaymentRequests as unknown as LoanPaymentRequest] : []);

  // If not authenticated, show loading or redirect
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <FeatureGuard feature="payments">
      <Box sx={{ p: 3, opacity: isAuthenticated() ? 1 : 0.5, pointerEvents: isAuthenticated() ? 'auto' : 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Payments
            </Typography>
          </Box>
          <UserTypeIndicator />
        </Box>

        {/* Loan Context Alert */}
        {loanContext?.trackingNumber && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Loan Context:</strong> You are viewing payments for loan tracking number: {loanContext.trackingNumber}
            </Typography>
          </Alert>
        )}

        {/* Access Level Information */}
        {!user?.accountNumber && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Access Level:</strong> You can make payments for loan applications, but full account features require account verification. 
              To unlock full access, please provide your SMEDCO account number or contact support.
            </Typography>
          </Alert>
        )}

        {/* Loan Application Payment Focus for users without account access */}
        {!user?.hasFullAccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Loan Application Payments:</strong> You can make payments for your loan applications, including processing fees and other charges. 
              Use the "Loan Payment Requests" tab below to view and process your outstanding payments.
            </Typography>
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Payment Statistics Cards - Only show for users with account access */}
        {user?.hasFullAccess && stats ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {stats.totalPayments}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Payments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {stats.successfulPayments}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Successful
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                    {stats.failedPayments}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Failed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Amount
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : user?.hasFullAccess && !stats ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              No payment activity yet. Your payment statistics will appear here after you make your first payment.
            </Typography>
          </Alert>
        ) : null}

        {/* Success Rate Indicator - Only show for users with account access */}
        {user?.hasFullAccess && stats && (
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">Payment Success Rate</Typography>
                </Box>
                <Chip
                  label={formatPercentage(stats.successRate)}
                  color={stats.successRate > 0.8 ? 'success' : stats.successRate > 0.6 ? 'warning' : 'error'}
                  size="medium"
                  sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => {
            // Check authentication before tab change
            if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              navigate('/login');
              return;
            }
            setActiveTab(newValue);
          }}>
            {/* Only show Payment History tab for users with account access */}
            {user?.hasFullAccess && (
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1 }} />
                    Payment History
                  </Box>
                } 
              />
            )}
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge badgeContent={normalizedLoanPaymentRequests.length} color="error">
                    <AssignmentIcon sx={{ mr: 1 }} />
                  </Badge>
                  Loan Payment Requests
                </Box>
              } 
            />
          </Tabs>
        </Box>

        {/* Payment History Table - Only show for users with account access */}
        {user?.hasFullAccess && activeTab === 0 && (
          <PaymentHistoryTable title="All Payment History" />
        )}

        {/* Interactive Loan Payment Section for users without account access */}
        {!user?.hasFullAccess && activeTab === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Loan Application Payments
            </Typography>
            
            {/* Search/Track Loan Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Find Your Loan Payment Requests
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your loan tracking number to see outstanding payments and make payments for processing fees.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    label="Loan Tracking Number"
                    placeholder="e.g., 682003"
                    value={trackingNumberInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      // Check authentication before input change
                      if (!isAuthenticated()) {

                        sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                        navigate('/login');
                        return;
                      }
                      setTrackingNumberInput(e.target.value);
                    }}
                    helperText="Enter the tracking number from your loan application"
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      // Check authentication before search
                      if (!isAuthenticated()) {

                        sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                        navigate('/login');
                        return;
                      }
                      
                      const trimmedInput = String(trackingNumberInput || '').trim();
                      if (trimmedInput) {
                        fetchLoanPaymentRequestsForTracking(trimmedInput);
                      }
                    }}
                    disabled={!String(trackingNumberInput || '').trim()}
                    startIcon={<SearchIcon />}
                  >
                    Find Payments
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Check authentication before clear
                      if (!isAuthenticated()) {

                        sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                        navigate('/login');
                        return;
                      }
                      
                      // Clear current input and results
                      setTrackingNumberInput('');
                      setLoanPaymentRequests([]);
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Payment Requests Display */}
            {loanPaymentRequests.length > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Outstanding Payment Requests ({loanPaymentRequests.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Check authentication before refresh
                      if (!isAuthenticated()) {

                        sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                        navigate('/login');
                        return;
                      }
                      
                      fetchLoanPaymentRequests();
                    }}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh
                    </Button>
                </Box>
                
                <List>
                  {loanPaymentRequests.map((payment, index) => (
                    <React.Fragment key={payment.id}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                          {getPaymentTypeIcon(payment.paymentType)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {payment.description}
                              </Typography>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                {formatCurrency(payment.amount, payment.currency)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <Chip
                                  label={payment.paymentType.replace('_', ' ').toUpperCase()}
                                  size="small"
                                  color={getPaymentTypeColor(payment.paymentType) as any}
                                  variant="outlined"
                                />
                                {payment.dueDate && (
                                  <Chip
                                    label={`Due: ${formatDate(payment.dueDate)}`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                                {payment.trackingNumber && (
                                  <Chip
                                    label={`Loan: ${payment.trackingNumber}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                              {payment.description}
                            </Typography>
                            {/* Payment Status Display */}
                            {paymentStatus[payment.id.toString()] && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={`Status: ${paymentStatus[payment.id.toString()]}`}
                                  size="small"
                                  color={
                                    paymentStatus[payment.id.toString()] === 'PAID' ? 'success' :
                                    paymentStatus[payment.id.toString()] === 'PENDING' ? 'warning' :
                                    paymentStatus[payment.id.toString()] === 'FAILED' ? 'error' : 'default'
                                  }
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        }
                      />
                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleMakePayment(payment)}
                          >
                            Make Payment
                          </Button>
                          {payment.id && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => checkPaymentStatus(payment.id.toString())}
                              disabled={checkingStatus[payment.id.toString()]}
                            >
                              {checkingStatus[payment.id.toString()] ? 'Checking...' : 'Check Status'}
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                      {index < loanPaymentRequests.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Payment Requests Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {trackingNumberInput 
                    ? `No outstanding payments found for tracking number: ${trackingNumberInput}`
                    : 'Enter a loan tracking number above to find payment requests for your loan application.'
                  }
                </Typography>
                {!trackingNumberInput && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/loan-application')}
                    startIcon={<AssignmentIcon />}
                  >
                    Go to Loan Applications
                  </Button>
                )}
              </Card>
            )}
          </Box>
        )}

        {/* Loan Payment Requests */}
        {(user?.hasFullAccess ? activeTab === 1 : activeTab === 0) && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Loan Payment Requests
            </Typography>
            
            {normalizedLoanPaymentRequests.length === 0 ? (
              <Alert severity="info">
                {user?.accountNumber
                  ? 'No outstanding payment requests found for your accounts.'
                  : 'No loan payment requests found. You can still pay for processing fees by tracking your loan under Loan Applications.'}
              </Alert>
            ) : (
              <List>
                {normalizedLoanPaymentRequests.map((payment, index) => (
                  <React.Fragment key={payment.id}>
                    <ListItem sx={{ px: 0, py: 2 }}>
                      <ListItemIcon>
                        {getPaymentTypeIcon(payment.paymentType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {payment.description}
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                              {formatCurrency(payment.amount, payment.currency)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                              <Chip
                                label={payment.paymentType.replace('_', ' ').toUpperCase()}
                                size="small"
                                color={getPaymentTypeColor(payment.paymentType) as any}
                                variant="outlined"
                              />
                              {payment.dueDate && (
                                <Chip
                                  label={`Due: ${formatDate(payment.dueDate)}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {payment.trackingNumber && (
                                <Chip
                                  label={`Loan: ${payment.trackingNumber}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {payment.description}
                            </Typography>
                            {/* Payment Status Display */}
                            {paymentStatus[payment.id.toString()] && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={`Status: ${paymentStatus[payment.id.toString()]}`}
                                  size="small"
                                  color={
                                    paymentStatus[payment.id.toString()] === 'PAID' ? 'success' :
                                    paymentStatus[payment.id.toString()] === 'PENDING' ? 'warning' :
                                    paymentStatus[payment.id.toString()] === 'FAILED' ? 'error' : 'default'
                                  }
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleMakePayment(payment)}
                        >
                          Make Payment
                        </Button>
                        {payment.id && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => checkPaymentStatus(payment.id.toString())}
                            disabled={checkingStatus[payment.id.toString()]}
                          >
                            {checkingStatus[payment.id.toString()] ? 'Checking...' : 'Check Status'}
                          </Button>
                        )}
                      </Box>
                    </ListItem>
                    {index < normalizedLoanPaymentRequests.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}
      </Box>
    </FeatureGuard>
  );
};

export default Payments;
